import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import { ProgressSpinner } from 'primereact/progressspinner';
import SelecaoInternacao from './SelecaoInternacao';
import { PendenciasWizard, setPendencia } from './ValidacaoWizard';
import {
  dominioHCFamema,
  dominioDemandaCirurgica,
  dominioOperaAspNet,
} from '../../config/database';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function pedidoCirurgiaVemDoModelo() {
  const pedidoCirurgiaVemDoModelo = JSON.parse(localStorage.getItem('pedidoCirurgiaVemDoModelo'));
  return pedidoCirurgiaVemDoModelo;
}

const headCells = [
  { id: 'ATENDIMENTO', numeric: false, disablePadding: true, label: 'Atendimentos' },
];

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align="left"
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
        color: theme.palette.secondary.main,
        backgroundColor: lighten(theme.palette.secondary.light, 0.85),
      }
      : {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark,
      },
  title: {
    flex: '1 1 100%',
  },
}));

const EnhancedTableToolbar = () => {
  const classes = useToolbarStyles();

  return (
    <Toolbar>
      <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
        Selecione o atendimento
      </Typography>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
    height: '10vh',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

export default function SelecaoAtendimento(props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('calories');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(6);
  const [atendimentos, setAtendimentos] = React.useState([]);
  const [internacao, setInternacao] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingInternacao, setLoadingInternacao] = React.useState(false);
  const { seq_paciente, closeModal } = props;

  useEffect(() => {
    setLoading(true);
    dominioHCFamema.get(`/api/listarAtendimentosPaciente/${seq_paciente}`).then(
      response => {
        setAtendimentos(response.data);
        setLoading(false);
        localStorage.setItem('temAtendimento', true);
      },
      () => {
        setLoading(false);
      }
    );

    setLoadingInternacao(true);
    dominioOperaAspNet.get(`/api/listarInternacoesPaciente/${seq_paciente}`).then(
      response => {
        setInternacao(response.data);
        setLoadingInternacao(false);
      },
      () => {
        setLoadingInternacao(false);
      }
    );
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = async (event, row) => {
    localStorage.setItem('atendimento', JSON.stringify(row));
    setPendencia(0, PendenciasWizard.atendimentoInternacao, 'pendenciasPaciente');
    setLoading(true);
    dominioHCFamema.get(`/api/listaexamesselecaoatendimentopera/${row.SEQ_ATENDIMENTO}`).then(
      async response => {
        let exames = [];
        await dominioDemandaCirurgica.get('/api/itemExame').then(resExames => {
          exames = resExames.data
            .filter(exame =>
              response.data.some(examesSihosp => exame.idLegado === examesSihosp.SEQ_EXAME)
            )
            .sort((exameA, exameB) => {
              if (exameA.descricao < exameB.descricao) {
                return -1;
              }
              if (exameA.descricao > exameB.descricao) {
                return 1;
              }
              return 0;
            });
        });
        localStorage.setItem('examesAtendimento', JSON.stringify(exames));
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );

    closeModal();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDataAtendimentoFormatada = data => {
    return new Date(data).toLocaleString();
  };

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, atendimentos.length - page * rowsPerPage);

  if (loading || loadingInternacao) {
    return <ProgressSpinner style={{ left: '45%' }} />;
  }

  return (
    <div className={classes.root}>
      {internacao.length >= 1 && !pedidoCirurgiaVemDoModelo() ? (
        <SelecaoInternacao internacao={internacao[0]} closeModal={closeModal} />
      ) : (
        <> </>
      )}
      <Paper className={classes.paper}>
        <EnhancedTableToolbar numSelected={selected.length} />
        <TableContainer style={{ maxHeight: '300px' }}>
          <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table">
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length + 1}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={atendimentos.length}
            />
            <TableBody>
              {stableSort(atendimentos, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(row => {
                  return (
                    <TableRow
                      hover
                      onClick={event => handleClick(event, row)}
                      tabIndex={-1}
                      key={row.name}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell align="left">
                        <div>
                          <span className="titulo-card-modal">
                            <strong>Data atendimento:</strong>
                          </span>
                          {getDataAtendimentoFormatada(row.DAT_HORA_ATENDIMENTO)}
                        </div>
                        <div>
                          <span className="titulo-card-modal">
                            <strong>Nº ficha:</strong>
                          </span>
                          {row.NRO_FAA}
                        </div>
                        <div>
                          <span className="titulo-card-modal">
                            <strong>Caráter:</strong>
                          </span>
                          {row.NOM_CARATER_ATENDIMENTO}
                        </div>
                        <div>
                          <span className="titulo-card-modal">
                            <strong>Especialidade:</strong>
                          </span>
                          {row.NOM_CENTRO_CUSTO}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[6]}
          component="div"
          count={atendimentos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
